import React, { useCallback } from 'react';
import { CubeIcon } from '@phosphor-icons/react';
import {
    showSiteViewerUnavailableModal,
    useSiteViewer3dBlocked,
} from '../../../../../helpers/siteViewer3dAccess';
import { openHoverJobViewer } from '../../../../../services/oneButtonServices/hoverServices';
import HoverLogoMark from '../HoverLogoMark';

interface DashboardAiRailPeekButtonProps {
    aiRailOpen: boolean;
    /** Peek only appears when the full dashboard overlay is open. */
    isDashboardOpen: boolean;
    theme: 'dark' | 'light';
    hoverAvailable: boolean;
    hoverJobId: string | null;
    /** Close dashboard, go to roof / 3D configure view. */
    onView3d: () => void;
}

/** Right-edge peek on the full dashboard: oneDRAW 3D viewer, or open Hover when a job exists. */
export const DashboardAiRailPeekButton: React.FC<DashboardAiRailPeekButtonProps> = ({
    aiRailOpen,
    isDashboardOpen,
    theme,
    hoverAvailable,
    hoverJobId,
    onView3d,
}) => {
    const { blocked: viewer3dBlocked, message: blockedMessage } = useSiteViewer3dBlocked();

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (hoverAvailable) {
                openHoverJobViewer(hoverJobId);
                return;
            }
            if (viewer3dBlocked) {
                showSiteViewerUnavailableModal(blockedMessage);
                return;
            }
            onView3d();
        },
        [hoverAvailable, hoverJobId, viewer3dBlocked, blockedMessage, onView3d]
    );

    if (!isDashboardOpen || aiRailOpen) return null;

    const peekLabel = hoverAvailable
        ? 'Open Hover Job'
        : viewer3dBlocked
          ? '3D viewer unavailable — click for details'
          : 'View 3D';

    return (
        <button
            type="button"
            className={`obd-ai-rail__peek obd-theme-${theme}${hoverAvailable ? ' obd-ai-rail__peek--hover obd-ai-rail__peek--hover-active' : ''}${!hoverAvailable && viewer3dBlocked ? ' obd-ai-rail__peek--blocked' : ''}`}
            onClick={handleClick}
            aria-label={peekLabel}
            title={hoverAvailable ? 'Open Hover property' : peekLabel === 'View 3D' ? 'Open 3D Viewer' : peekLabel}
        >
            {hoverAvailable ? (
                <>
                    <HoverLogoMark
                        size="icon"
                        tone="light"
                        title="HOVER"
                        className="obd-hover-logo-mark--active"
                    />
                    <span>HOVER</span>
                </>
            ) : (
                <>
                    <CubeIcon size={22} weight="duotone" aria-hidden />
                    <span>oneDRAW</span>
                </>
            )}
        </button>
    );
};
